import type { ReactNode } from 'react';
import * as s from './InlineStatus.css';

export type InlineStatusKind = 'idle' | 'pending' | 'success' | 'error';

export interface InlineStatusProps {
  status: InlineStatusKind;
  message?: ReactNode;
  showIcon?: boolean;
  /** 'auto' (default): error → 'assertive', else 'polite'. */
  ariaLive?: 'polite' | 'assertive' | 'auto';
  className?: string;
}

const DEFAULT_MESSAGE: Record<InlineStatusKind, string> = {
  idle: '',
  pending: '저장 중…',
  success: '저장됨',
  error: '저장 실패',
};

const ICON: Record<InlineStatusKind, string> = {
  idle: '',
  pending: '⋯',
  success: '✓',
  error: '!',
};

/**
 * Inline status feedback (필드 옆 transient feedback). Toast 와 다름 — 컨텍스트
 * 옆에 즉시 부착되어 전이 상태 (idle / pending / success / error) 표시.
 *
 * - status='idle' + message 미지정 → 렌더 안 함.
 * - role: pending/success → status, error → alert (a11y 정합).
 * - aria-live='auto' (default): error 'assertive', 그 외 'polite'.
 */
export function InlineStatus({
  status,
  message,
  showIcon = true,
  ariaLive = 'auto',
  className,
}: InlineStatusProps) {
  const text = message ?? DEFAULT_MESSAGE[status];
  if (status === 'idle' && (text === '' || text === undefined || text === null)) return null;

  const role = status === 'error' ? 'alert' : 'status';
  const live = ariaLive === 'auto' ? (status === 'error' ? 'assertive' : 'polite') : ariaLive;

  return (
    <span
      role={role}
      aria-live={live}
      aria-atomic="true"
      className={`${s.root} ${s.status[status]}${className ? ` ${className}` : ''}`}
    >
      {showIcon && ICON[status] && (
        <span className={s.icon} aria-hidden="true">
          {ICON[status]}
        </span>
      )}
      {text !== '' && <span>{text}</span>}
    </span>
  );
}
